import type { Me, Tenant } from '@bk/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiRequestError, session } from './lib/api';
import { useAppOutdated } from './lib/app-version';
import { lastTenant } from './lib/last-tenant';
import { navigate, useRoute } from './lib/router';
import { AuthorizePage } from './pages/AuthorizePage';
import { LinePage } from './pages/LinePage';
import { LoginPage } from './pages/LoginPage';
import { JoinPersonPage } from './pages/JoinPersonPage';
import { TenantShell } from './pages/tenant/Shell';
import { PreparazionePage } from './pages/Preparazione';
import { TenantsPage } from './pages/TenantsPage';
import { SettingsPage } from './pages/SettingsPage';
import { TopBar, useTenants } from './pages/TopBar';
import { AppMark, PageLoader } from './ui';

/** Pagina fuori da un'azienda (nessuna azienda ancora, impostazioni, preparazione): stessa barra in alto, senza menu. */
function PlainLayout({ me, current, onLogout, children }: { me: Me; current: Tenant | null; onLogout: () => void; children: ReactNode }) {
  return (
    <div className="mx-auto max-w-5xl px-3 pb-6 pt-3 lg:pt-4">
      <TopBar
        me={me}
        current={current}
        onLogout={onLogout}
        // Dentro un'azienda (anche in preparazione) il suo logo o la sua iniziale, mai il segno del prodotto (ADR 0152).
        logo={current ? undefined : <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-accent text-white"><AppMark className="h-full w-full" /></span>}
      />
      {children}
    </div>
  );
}

/** Nessuna azienda attiva, o si riparte da un'altra partita IVA: l'ingresso della Fase 0 (ADR 0201). */
function WelcomePage({ riparti = false }: { riparti?: boolean }) {
  return <TenantsPage riparti={riparti} />;
}

/** Avviso discreto quando online c'è una versione più recente di quella in uso. */
function UpdateBanner() {
  const outdated = useAppOutdated();
  if (!outdated) return null;
  return (
    <div className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
      <div className="flex items-center gap-3 rounded-full bg-ink px-4 py-2 text-sm text-white shadow-lg">
        È disponibile una nuova versione.
        <button className="font-semibold underline underline-offset-2" onClick={() => window.location.reload()}>
          Aggiorna
        </button>
      </div>
    </div>
  );
}

export function App() {
  const route = useRoute();
  return (
    <>
      {/* La pagina della linea operativa è pubblica: basta il link personale. */}
      {route[0] === 'l' && route[1] ? (
        <LinePage token={route[1]} />
      ) : (
        <AppContent />
      )}
      <UpdateBanner />
    </>
  );
}

function AppContent() {
  const queryClient = useQueryClient();
  // Stato React della sessione: il refresh token nel browser non provoca da solo un nuovo render.
  const [authenticated, setAuthenticated] = useState(session.hasRefresh());
  // Si torna al login solo se la sessione non vale più: un limite di richieste o la rete si riprovano (ADR 0051).
  const rejected = (e: unknown) => e instanceof ApiRequestError && e.status === 401;
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api<Me>('/auth/me'),
    enabled: authenticated,
    retry: (n, e) => !rejected(e) && n < 5,
    retryDelay: (n) => Math.min(2000 * 2 ** n, 20_000),
  });
  const route = useRoute();
  const tenantId = route[0] === 't' ? route[1] : undefined;
  const tenants = useTenants(authenticated && !!me.data);
  const active = (tenants.data ?? []).filter((t) => t.status === 'active');
  // All'ingresso (#/) chi ha aziende entra nell'ultima aperta, o nella prima.
  const entry = !route.length && !!active.length;
  const firstActive = (active.find((t) => t.id === lastTenant()) ?? active[0])?.id;
  useEffect(() => {
    if (entry && firstActive) window.location.replace(`#/t/${firstActive}`);
  }, [entry, firstActive]);

  if (!authenticated || me.isError) {
    return (
      <LoginPage
        onAuthenticated={() => {
          // Con una vecchia sessione scaduta `authenticated` era già vero: senza ricaricare «me» si resterebbe sul login.
          void queryClient.resetQueries({ queryKey: ['me'] });
          setAuthenticated(true);
        }}
      />
    );
  }
  if (!me.data) return <PageLoader />;

  async function logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    session.clear();
    queryClient.clear();
    setAuthenticated(false);
    // Cambiare account durante un invito deve riportare allo stesso invito dopo l'accesso.
    if (route[0] !== 'invito') navigate('/');
  }

  // Consenso OAuth per un assistente AI (dopo il login, se serviva, si torna qui).
  if (route[0]?.startsWith('autorizza')) return <AuthorizePage />;
  if (route[0] === 'invito' && route[1] && route[2]) return <JoinPersonPage key={`${route[1]}/${route[2]}`} tenantId={route[1]} token={route[2]} me={me.data} onLogout={logout} />;
  if (tenants.isLoading || entry) return <PageLoader />;

  const current = tenantId ? active.find((x) => x.id === tenantId) : undefined;
  if (current?.access_scope === 'tasks' || current?.access_scope === 'data') return <TenantShell tenant={current} me={me.data} route={route.slice(2)} onLogout={logout} />;

  // Azienda appena creata: la Fase 0 (ADR 0201), che poi apre da sola il gestionale.
  if (tenantId && route[2] === 'preparazione') return <PreparazionePage tenantId={tenantId} />;
  // Si riparte dalla partita IVA giusta, o l'elenco delle aziende.
  if (route[0] === 'riparti' || route[0] === 'aziende') return <WelcomePage riparti={route[0] === 'riparti'} />;

  if (current) return <TenantShell tenant={current} me={me.data} route={route.slice(2)} onLogout={logout} />;

  // Fuori da un'azienda: le impostazioni, o l'ingresso di chi non ha ancora un'azienda attiva.
  return (
    <PlainLayout me={me.data} current={null} onLogout={logout}>
      {route[0] === 'impostazioni' ? <SettingsPage me={me.data} section={route[1]} base="/impostazioni" /> : <WelcomePage />}
    </PlainLayout>
  );
}
